import {memo, useCallback} from 'react'

import {openDialog} from '../../../actions'
import {getBuildType} from '../../../selectors'
import {restApi} from '../../../services/rest'
import type {BuildId, TestOccurrenceId} from '../../../types'
import {DialogType, stringifyId} from '../../../types'
import {assignTestInvestigations} from '../../packages/Tests/Tests.actions'
import {getTestOccurenceArg} from '../../packages/Tests/Tests.rest'
import {
  getFlakyTestData,
  getTestOccurrence,
  getTestOccurrenceInvestigations,
} from '../../packages/Tests/Tests.selectors'

import Investigations from './Investigations'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

type Props = {
  className?: string
  iconClassName?: string
  buildId: BuildId
  testOccurrenceId?: TestOccurrenceId
  isShowOnlyIcon?: boolean
}

function InvestigationsWrapper(props: Props) {
  const dispatch = useAppDispatch()
  const {className, iconClassName, buildId, testOccurrenceId, isShowOnlyIcon} = props

  const testOccurrence = useAppSelector(state => getTestOccurrence(state, testOccurrenceId))
  const {build, test, currentlyInvestigated} = testOccurrence ?? {}
  const {buildTypeId} = build ?? {}
  const {id: testId} = test ?? {}

  const {projectId} = useAppSelector(state => getBuildType(state, buildTypeId)) ?? {}
  const {isFlaky} = useAppSelector(state => getFlakyTestData(state, buildId, testId)) ?? {}
  const investigations = useAppSelector(state =>
    getTestOccurrenceInvestigations(state, testOccurrenceId),
  )
  const [fetchTestOccurrence] =
    restApi.endpoints.getTestOccurrenceNormalized.useLazyQuerySubscription()
  const onAssignInvestigation = useCallback(
    (presetFix = false) => {
      if (testId != null && projectId != null) {
        const submitHandler = () => {
          testOccurrenceId != null &&
            fetchTestOccurrence(
              getTestOccurenceArg(testOccurrenceId, {
                withNewFailure: true,
                withMetadataCount: true,
                withNextFixed: true,
                withFirstFailed: true,
                withMuteInfo: true,
                withInvestigationInfo: true,
              }),
            )
        }
        dispatch(
          assignTestInvestigations(
            projectId,
            [testId],
            isFlaky ? [testId] : [],
            [buildId],
            presetFix,
            submitHandler,
          ),
        )
      }
    },
    [testId, projectId, dispatch, isFlaky, buildId, testOccurrenceId, fetchTestOccurrence],
  )

  const onShowInvestigationHistory = useCallback(() => {
    if (testId != null) {
      dispatch(openDialog(stringifyId(testId), DialogType.INVESTIGATION_HISTORY))
    }
  }, [dispatch, testId])

  return (
    <Investigations
      className={className}
      iconClassName={iconClassName}
      projectId={projectId}
      buildTypeId={buildTypeId}
      investigations={investigations}
      currentlyInvestigated={currentlyInvestigated}
      isShowOnlyIcon={isShowOnlyIcon}
      onAssignInvestigation={onAssignInvestigation}
      onShowInvestigationHistory={onShowInvestigationHistory}
    />
  )
}

export default memo(InvestigationsWrapper)
